<template>
    <div class="CEmbedVideo relative overflow-hidden" :class="responsive ? 'w-full aspect-video' : ''">
        <client-only>
            <!-- default: lite mode -->
            <!-- lite-youtube's stylesheet caps it at 720px; let the wrapper decide the width -->
            <lite-youtube
                v-if="youtubeId && lite"
                class="block size-full"
                style="max-width: none"
                :videoid="youtubeId"
                nocookie
                :playlabel="title || 'Prehrať video'"
                :params="ytParams"
                @click="playing"
            />

            <!-- non-lite iframe (use where you need md crop behavior) -->
            <CookieIframe
                v-else-if="youtubeId"
                class="embed-responsive-item"
                :title="title || 'YouTube video'"
                :src="`https://www.youtube-nocookie.com/embed/${youtubeId}?${ytParams}`"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
                referrerpolicy="strict-origin-when-cross-origin"
                :width="responsive ? '100%' : width"
                :height="responsive ? '100%' : height"
            />

            <!-- generic fallback -->
            <CookieIframe
                v-else
                class="embed-responsive-item"
                :title="title || 'Embedded video'"
                :src="src"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
                referrerpolicy="strict-origin-when-cross-origin"
                :width="responsive ? '100%' : width"
                :height="responsive ? '100%' : height"
            />
        </client-only>
    </div>
</template>

<script setup lang="ts">
import { getYoutubeVideoId } from '#shared/media'

interface EmbedVideoTrack {
    videoType: string
    recordingType: string
    oppType: string
    oppName: string
    oppOrg: string
    oppId: string | 'static'
}

const props = withDefaults(
    defineProps<{
        src?: string
        videoId?: string
        title?: string
        responsive?: boolean
        width?: number | string
        height?: number | string
        lite?: boolean // default true; set false where you need md crop
        trackData?: EmbedVideoTrack
    }>(),
    {
        src: '',
        videoId: '',
        title: '',
        responsive: true,
        width: 560,
        height: 315,
        lite: true,
        trackData: undefined
    }
)

const youtubeId = computed(() => props.videoId || getYoutubeVideoId(props.src) || '')

const ytParams = computed(() => {
    const p = new URLSearchParams()
    p.set('rel', '0')
    p.set('playsinline', '1')
    p.set('modestbranding', '1')
    p.set('controls', '1')
    return p.toString()
})

function trackVideoControls(action: string) {
    if (import.meta.client && props.trackData?.videoType && (window as any).$exponea) {
        ;(window as any).$exponea.track('video_controls', {
            video_type: props.trackData.videoType,
            video_action: action,
            opp_type: props.trackData.oppType,
            opp_name: props.trackData.oppName,
            opp_organisation: props.trackData.oppOrg,
            opp_id: props.trackData.oppId
        })
    }
}
const playing = () => trackVideoControls('play')
</script>

<style scoped>
.embed-responsive-item {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
}
</style>
